// Forgot-password screen for the carer portal. Sends through the same
// /auth/forgot-password endpoint the coordinator app uses; the API always
// returns 200 to prevent email enumeration.

import { useState, type JSX, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { carerApi } from "../../lib/carerApi.js";

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function CarerForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await carerApi.forgotPassword(email.trim());
      setSubmitted(true);
    } catch {
      setError("Could not send reset link. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy-800 flex flex-col items-center justify-center p-4">
      <div className="mb-8 text-center">
        <h1 className="text-white text-2xl font-bold">HCM</h1>
        <p className="text-navy-200 text-sm mt-1">Care Worker Portal</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Reset password</h2>

        {submitted ? (
          <>
            <p className="text-sm text-slate-600 mb-4">
              If an account exists for <strong>{email}</strong>, a reset link has been sent. The link expires in 1 hour.
            </p>
            <Link
              to="/carer/login"
              className="block w-full text-center py-3 px-4 rounded-xl bg-navy-700 text-white font-semibold text-base hover:bg-navy-800"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">
              Enter your work email — we'll send you a reset link.
            </p>
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
              <div>
                <label htmlFor="carer-fp-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="carer-fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com"
                  autoComplete="email"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base
                             focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                />
              </div>

              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || email.trim().length === 0}
                className="w-full py-3 px-4 rounded-xl bg-navy-700 text-white font-semibold text-base
                           hover:bg-navy-800 active:bg-navy-900 disabled:opacity-50 transition-colors"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>

              <Link to="/carer/login" className="block text-center text-sm text-slate-500 hover:text-slate-700">
                ← Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
